import { useEffect } from "react";
import {
  CHART_RECOVERY_DELAY_MS,
  CHART_RECOVERY_MIN_INTERVAL_MS,
  CHART_SLEEP_GAP_MS,
  CHART_HEARTBEAT_INTERVAL_MS,
} from "../constants";

/**
 * Handles chart data recovery after the browser tab/window was suspended
 * (sleep, background tab, device lock).
 *
 * Strategy:
 * - visibilitychange: if the tab was hidden for >= CHART_SLEEP_GAP_MS, schedule recovery.
 * - resume / focus / pageshow: if inactive for >= CHART_SLEEP_GAP_MS, schedule recovery.
 * - heartbeat: JS timers stop during sleep; a large gap between ticks means the
 *   device just woke up.
 * Recovery reloads the history and resubscribes to the stream (engine.reloadData),
 * so the chart catches up with the bars missed during sleep.
 *
 * @param {object|null} engine - ChartEngine from useChartEngine, null until created
 */
const useChartRecovery = (engine) => {
  useEffect(() => {
    if (!engine) return undefined;

    let hiddenAt = document.visibilityState === "hidden" ? Date.now() : null;
    let lastHeartbeatAt = Date.now();
    let lastRecoveryAt = 0;
    let recoveryTimeout = null;

    const recoverChartData = () => {
      const now = Date.now();

      if (
        document.visibilityState !== "visible" ||
        now - lastRecoveryAt < CHART_RECOVERY_MIN_INTERVAL_MS
      ) {
        return;
      }

      lastRecoveryAt = now;
      engine.reloadData();
    };

    const scheduleChartRecovery = () => {
      if (recoveryTimeout) {
        clearTimeout(recoveryTimeout);
      }

      recoveryTimeout = setTimeout(() => {
        recoveryTimeout = null;
        recoverChartData();
      }, CHART_RECOVERY_DELAY_MS);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }

      if (document.visibilityState !== "visible") return;

      const now = Date.now();
      const hiddenDuration = hiddenAt == null ? 0 : now - hiddenAt;

      hiddenAt = null;
      lastHeartbeatAt = now;

      if (hiddenDuration >= CHART_SLEEP_GAP_MS) {
        scheduleChartRecovery();
      }
    };

    const handlePageResume = () => {
      const now = Date.now();
      const inactiveDuration = now - lastHeartbeatAt;

      lastHeartbeatAt = now;

      if (
        document.visibilityState === "visible" &&
        inactiveDuration >= CHART_SLEEP_GAP_MS
      ) {
        scheduleChartRecovery();
      }
    };

    const heartbeatInterval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - lastHeartbeatAt;

      lastHeartbeatAt = now;

      /*
       * After laptop sleep, JS timers pause.
       * A large gap between heartbeats means the page just resumed
       * even if the browser did not fire visibilitychange.
       */
      if (document.visibilityState === "visible" && elapsed >= CHART_SLEEP_GAP_MS) {
        scheduleChartRecovery();
      }
    }, CHART_HEARTBEAT_INTERVAL_MS);

    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("resume", handlePageResume);
    window.addEventListener("focus", handlePageResume);
    window.addEventListener("pageshow", handlePageResume);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("resume", handlePageResume);
      window.removeEventListener("focus", handlePageResume);
      window.removeEventListener("pageshow", handlePageResume);

      clearInterval(heartbeatInterval);

      if (recoveryTimeout) {
        clearTimeout(recoveryTimeout);
      }
    };
  }, [engine]);
};

export default useChartRecovery;
